import React, { useState, useCallback, useEffect } from 'react';
import Banner from '../Banner/Banner';
import UploadZone from '../FileUpload/UploadZone';
import FileManager, { FileUploadState } from '../FileUpload/FileManager';
import FilePreview from '../FileUpload/FilePreview';
import { Button } from '../Button';
import GBTAEngagementScore from './GBTAEngagementScore';
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  StyledCloseIcon,
  ModalContent,
  SectionContainer,
  SectionContent,
  SectionText,
  BulletList,
  BulletItem,
  DownloadButton,
} from './GBTAUploadModal.styled';
import { useGBTA } from '../../../hooks';
import { API_ENDPOINTS } from '../../../services/apiEndpoints';
import { useHotelQuestionnaireContext } from '../../../contexts/HotelQuestionnaireContext';

interface GBTAUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  hotelId: string;
}

const GBTAUploadModal: React.FC<GBTAUploadModalProps> = ({
  isOpen,
  onClose,
  hotelId,
}) => {
  const [uploadedFiles, setUploadedFiles] = useState<FileUploadState[]>([]);
  const [previewFile, setPreviewFile] = useState<FileUploadState | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  
  // Get GBTA data from hook - skip initial fetch, we'll fetch when modal opens
  const {
    gbtaData,
    isUploading,
    isDeleting,
    uploadGbta,
    deleteGbta,
    fetchGbta,
  } = useGBTA({ hotelId, skipInitialFetch: true });
  
  // Get questionnaire context to refetch after upload/delete
  const { state, actions } = useHotelQuestionnaireContext();
  const currentStepKey = state.stepsData?.currentStepKey;

  // Get existing GBTA file (single file from new structure)
  const existingGbtaFile = gbtaData || null;

  // Add files to state without uploading immediately
  const handleFilesSelected = useCallback(async (files: FileList) => {
    // Check if we already have an existing GBTA file
    if (existingGbtaFile) {
      console.error('A GBTA file already exists. Please remove it first before uploading a new one.');
      return;
    }

    // Check if we already have a file in upload state
    if (uploadedFiles.length > 0) {
      console.error('Only one file can be uploaded at a time. Please remove the existing file first.');
      return;
    }

    // Check if trying to upload more than 1 file
    if (files.length > 1) {
      console.error('Only one file can be uploaded at a time.');
      return;
    }

    const file = files[0];
    const validExtensions = ['.xls', '.xlsx', '.csv'];
    const fileExtension = file.name
      .toLowerCase()
      .substring(file.name.lastIndexOf('.'));

    // Validate file type
    if (!validExtensions.includes(fileExtension)) {
      console.error('Please select a valid file (.xls, .xlsx, .csv)');
      return;
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      console.error('File size must be under 10MB');
      return;
    }

    try {
      // Create image preview if it's an image file (though unlikely for Excel/CSV)
      let preview: string | undefined;
      const isImageFile = file.type.startsWith('image/');
      if (isImageFile) {
        preview = await createImagePreview(file);
      }

      // Create file state for display
      const newFileState: FileUploadState = {
        id: `${Date.now()}_0`,
        file,
        status: 'initial',
        progress: 0,
        preview,
      };

      // Add to uploaded files state
      setUploadedFiles([newFileState]);
    } catch (error) {
      console.error('File preparation failed:', error);
    }
  }, [existingGbtaFile, uploadedFiles.length]);

  // Create image preview
  const createImagePreview = (file: File): Promise<string> => {
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = e => resolve(e.target?.result as string);
      reader.readAsDataURL(file);
    });
  };

  // Upload the file when user clicks upload button
  const handleUpload = async () => {
    const fileToUpload = uploadedFiles.find(f => f.status === 'initial');
    
    if (!fileToUpload) {
      console.error('No file to upload');
      return;
    }

    try {
      // Update status to uploading
      setUploadedFiles(prev =>
        prev.map(f =>
          f.id === fileToUpload.id
            ? { ...f, status: 'uploading', progress: 0 }
            : f
        )
      );

      // Upload using hook with progress tracking
      await uploadGbta(fileToUpload.file, (progress) => {
        setUploadedFiles(prev =>
          prev.map(f =>
            f.id === fileToUpload.id ? { ...f, progress } : f
          )
        );
      });

      // Update status to completed
      setUploadedFiles(prev =>
        prev.map(f =>
          f.id === fileToUpload.id
            ? { ...f, status: 'completed', progress: 100 }
            : f
        )
      );

      // Clear the uploaded files state since the file is now part of GBTA data
      setUploadedFiles([]);
      
      // Refetch questionnaire at current step to reflect GBTA upload
      if (currentStepKey) {
        await actions.fetchQuestionnaire(currentStepKey);
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || 'Upload failed';
      
      // Clear the uploaded files state on error
      setUploadedFiles([]);
    }
  };

  // Remove file from state or delete GBTA attachment
  const removeFile = async (fileId: string) => {
    // Check if this is a local file being uploaded
    const localFile = uploadedFiles.find(f => f.id === fileId);
    if (localFile) {
      // Remove from local state only
      setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
      return;
    }

    // Otherwise, this is an existing GBTA file - delete it from server
    try {
      await deleteGbta(fileId);
      
      // Refetch questionnaire at current step to reflect GBTA deletion
      if (currentStepKey) {
        await actions.fetchQuestionnaire(currentStepKey);
      }
    } catch (err) {
      console.error('Failed to delete GBTA file:', err);
    }
  };

  // View file - opens preview modal
  const viewFile = (file: FileUploadState) => {
    setPreviewFile(file);
    setShowPreview(true);
  };

  // Handle preview close
  const handlePreviewClose = () => {
    setShowPreview(false);
    setPreviewFile(null);
  };

  // Fetch GBTA data when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchGbta();
    }
  }, [isOpen, fetchGbta]);

  if (!isOpen) return null;

  return (
    <ModalOverlay>
      <ModalContainer onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>GBTA Data Upload</ModalTitle>
          <CloseButton onClick={onClose}>
            <StyledCloseIcon />
          </CloseButton>
        </ModalHeader>

        <ModalContent>
          <SectionContainer>
            
            {/* What's this section */}
            <Banner
              titleBadgeText="What's this?"
              expandButtonText="More"
              collapseButtonText="Less"
              initialExpanded={true}
              badgeStyles={{
                $badgeBackgroundColor: 'var(--color-planet-green)',
                $badgeTextColor: 'white',
              }}
              headerComponent={
                <SectionContent>
                  <SectionText>
                    <strong>
                      We now accept the answers to your GBTA Sustainable
                      Procurement Standards for Hotels Version 1.0.
                    </strong>
                  </SectionText>
                  <SectionText>
                    This feature may help expedite your Alō Index submission
                    process.
                  </SectionText>
                  <SectionText>
                    Here is what you need to know before using this feature:
                  </SectionText>
                </SectionContent>
              }
              contentComponent={
                <SectionContent>
                  <BulletList>
                    <BulletItem>
                      You must use your answers from the GBTA Sustainable
                      Procurement Standards for Hotels Version 1.0 released by
                      GBTA on June 10, 2024.
                    </BulletItem>
                    <BulletItem>
                      Because Alō Index is a deeper assessment than the GBTA
                      Standards, it is not recommended to use your GBTA answers
                      to complete Alō Index.
                    </BulletItem>
                    <BulletItem>
                      After uploading GBTA answers, we encourage every hotel to
                      carefully review how the GBTA answers have been populated
                      within the Alō assessment and, where needed, provide
                      additional information about the strength of your hotel
                      initiatives.
                    </BulletItem>
                  </BulletList>
                  <SectionText>
                    If you have previously submitted your hotel's information
                    for assessment by GBTA, you can use the same data here as a
                    starting point for your Alō Index questionnaire.
                  </SectionText>
                </SectionContent>
              }
            />

            {/* Download Template section */}
            <Banner
              titleBadgeText="Download Template"
              expandButtonText="More"
              collapseButtonText="Less"
              initialExpanded={true}
              containerStyles={{
                $backgroundColor: 'var(--color-policy-orange-light)',
                $borderColor: 'var(--color-policy-orange)',
              }}
              badgeStyles={{
                $badgeBackgroundColor: 'var(--color-policy-orange)',
                $badgeTextColor: 'white',
              }}
              headerComponent={
                <SectionContent>
                  <SectionText>
                    <strong>Download our GBTA Template</strong>
                  </SectionText>
                </SectionContent>
              }
              contentComponent={
                <SectionContent>
                  <SectionText>
                    Download our GBTA Template to fill in your hotel's data.
                    Once you have filled in the template, you can upload it
                    below.
                  </SectionText>
                  <SectionText>
                    The template is designed to help you map your GBTA data to
                    the Alō Index questionnaire. Make sure to only fill in your
                    hotel's data in the correct column.
                  </SectionText>
                  <DownloadButton
                    href={`${process.env.REACT_APP_API_URL}${API_ENDPOINTS.gbta.template}`}
                  >
                    Download Template
                  </DownloadButton>
                </SectionContent>
              }
            />

            {/* Upload Your Data section */}
            <Banner
              titleBadgeText="Upload Your Data"
              expandButtonText="More"
              collapseButtonText="Less"
              initialExpanded={true}
              containerStyles={{
                $backgroundColor: 'var(--color-white)',
                $borderColor: 'var(--color-gray-300)',
                $borderRadius: '0.5rem',
                // $padding: '1rem',
                // $margin: '1rem 0 0 0',
              }}
              badgeStyles={{
                $badgeBackgroundColor: 'var(--color-gray-600)',
                $badgeTextColor: 'white',
              }}
              headerComponent={
                <SectionContent>
                  <SectionText>
                    <strong>Upload your hotel's GBTA data</strong>
                  </SectionText>
                  {/* {uploadedFiles.length === 0 ? (
                    <NoFilesText>No files uploaded</NoFilesText>
                  ) : (
                    <SectionText>{uploadedFiles.length} file(s) selected</SectionText>
                  )} */}
                </SectionContent>
              }
              contentComponent={
                <SectionContent className='mt-4 w-full'>
                  {/* Show existing GBTA file if present */}
                  {existingGbtaFile && (
                    <div style={{ marginBottom: '1rem' }}>
                      <SectionText style={{ marginBottom: '0.5rem' }}>
                        <strong>Current GBTA File:</strong>
                      </SectionText>
                      <FileManager
                        files={[{
                          id: existingGbtaFile.attachment_id,
                          file: new File([], existingGbtaFile.original_filename),
                          status: isDeleting ? 'deleting' : 'completed',
                          progress: 100,
                          previewUrl: existingGbtaFile.preview_url,
                        }]}
                        onRemoveFile={removeFile}
                        onViewFile={viewFile}
                        disabled={isDeleting}
                      />
                    </div>
                  )}

                  {/* Show upload zone only if no existing file and no files being uploaded */}
                  {!existingGbtaFile && uploadedFiles.length === 0 && (
                    <UploadZone
                      onFilesSelected={handleFilesSelected}
                      maxFiles={1}
                      maxFileSize={10}  
                      acceptedTypes={['.xls', '.xlsx', '.csv']}
                      disabled={isUploading}
                    />
                  )}

                  {/* Show new files being uploaded */}
                  <div style={{ position: 'relative' }}>
                    <FileManager
                      files={uploadedFiles}
                      onRemoveFile={removeFile}
                      onViewFile={viewFile}
                    />

                    {/* Upload Button - Show only when there's a file ready to upload */}
                    {uploadedFiles.some(f => f.status === 'initial' || f.status === 'pending' || f.status === 'uploading') && (
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'flex-end', 
                        marginTop: '1rem' 
                      }}>
                        <Button
                          onClick={handleUpload}
                          disabled={isUploading}
                          loading={isUploading}
                          variant="primary"
                          size="md"
                        >
                          {isUploading ? 'Uploading...' : 'Upload File'}
                        </Button>
                      </div>
                    )}
                  </div>
                </SectionContent>
              }
            />
{/* Success Banner with Engagement Score - Show when GBTA data exists */}
            {existingGbtaFile && existingGbtaFile.engagement_score && (
              <Banner
                titleBadgeText="Info"
                expandButtonText="Less"
                collapseButtonText="More"
                initialExpanded={true}
                containerStyles={{
                  $backgroundColor: '#D1FAE5', // Light green
                  $borderColor: '#10B981', // Green
                  // $marginBottom: '1.5rem',
                }}
                badgeStyles={{
                  $badgeBackgroundColor: '#10B981', // Green
                  $badgeTextColor: 'white',
                }}
                headerComponent={
                  <SectionContent>
                    <SectionText>
                      <strong>GBTA data has been successfully uploaded!</strong>
                    </SectionText>
                    <SectionText style={{ fontSize: '0.875rem' }}>
                      Below is a summary of how your GBTA answers have helped to complete the Alō Index assessment.
                    </SectionText>
                  </SectionContent>
                }
                contentComponent={
                  <SectionContent>
                    <GBTAEngagementScore engagementScore={existingGbtaFile.engagement_score} onClose={onClose} />
                  </SectionContent>
                }
              />
            )}

          </SectionContainer>
        </ModalContent>
      </ModalContainer>
      
      {/* File Preview Modal */}
      <FilePreview
        file={previewFile}
        isOpen={showPreview}
        onClose={handlePreviewClose}
      />
    </ModalOverlay>
  );
};

export default GBTAUploadModal;
